import type { GQL_ID } from '@@partner:types:graphql'
import { type CrudFormProps } from '@@partner:types:app'

import React, { useCallback, useEffect, useState } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makeTextMaterialThemeFormState,
  mapApiDataToState,
  mapStateToApiVariable,
  TextMaterialThemeFormState
} from './TextMaterialThemeFormState'

import { Col, DatePicker, Form, Grid, Input, InputGroup, Nav, Panel, SelectPicker, Tag } from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import { useFetchSingleTextMaterialTheme } from '@@partner:graphql/query/textMaterial/themes/singleTextMaterialTheme.gql'
import { getSearchableTextMaterialStatus } from '@@partner:helpers/textMaterial'
import { useMutateUpdateTextMaterialTheme } from '@@partner:graphql/mutation/textMaterial/updateTextMaterialTheme.gql'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'

export interface ThemeFormProps extends CrudFormProps {
  themeId?: GQL_ID
}

const AVAILABLE_TEXT_CATEGORY_STATUSES = getSearchableTextMaterialStatus()


export default function TextMaterialThemeForm ({
  themeId,
  onSaved
}: ThemeFormProps) {

  const { formState, setFormState, setFormRef } =
    useValidatableForm<TextMaterialThemeFormState>(makeTextMaterialThemeFormState)
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>('ru')
  
  const {
    data,
    loading: loadingTextMaterialTheme,
    error: errorLoadingTextMaterialTheme
  } = useFetchSingleTextMaterialTheme({
    id: themeId
  })

  const [ saveTextMaterialTheme, { loading: loadingSaveTextTheme, error: errorSavingTextTheme } ] =
    useMutateUpdateTextMaterialTheme()

  useEffect(() => {
    if (!loadingTextMaterialTheme && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.singleTextMaterialTheme))
      )
    }
  }, [ data, loadingTextMaterialTheme, setFormState ])

  const textMaterial = data?.singleTextMaterialTheme

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )
  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveTextMaterialTheme({
          variables: {
            textMaterialThemeInfo: mapStateToApiVariable(formState),
            upsert: themeId ? false : true
          }
        })
          .then(({ data }) => {
            if (data?.updateTextMaterialTheme) {
              notifyOk('Категория сохранена')
              setFormState((state) =>
                mergeLeftOnly([ 'id' ], state, mapApiDataToState(data.updateTextMaterialTheme))
              )
  
              onSaved?.(data?.updateTextMaterialTheme.id)
            }
            
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveTextMaterialTheme, formState, setFormState, onSaved, themeId ]
  )

  if (errorLoadingTextMaterialTheme) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить категорию #${themeId} для редактирования`}
        error={errorLoadingTextMaterialTheme}
      />
    )
  }

  if (loadingTextMaterialTheme) {
    return <AppLoader content={'Загрузка категории' } />
  }

  if (errorLoadingTextMaterialTheme) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные категории #${themeId} для редактирования`}
        error={errorLoadingTextMaterialTheme}
      />
    )
  }
  
  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      layout='vertical'
      fluid
    >
      
      <Grid fluid>
        <Col xs={24} md={16}>
          <Panel
            shaded
            className='my-5'
          >
            <Form.Group>
              <Form.ControlLabel>Название (техническое)</Form.ControlLabel>
              <Form.Control 
                name="name" 
                placeholder="Name" 
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Имя в URL</Form.ControlLabel>
              <Form.Control 
                name="slug" 
                placeholder="Имя в URL" 
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Статус</Form.ControlLabel>
              <Form.Control 
                name='status' 
                value={formState.status}
                accepter={SelectPicker} 
                data={AVAILABLE_TEXT_CATEGORY_STATUSES} 
              />
            </Form.Group>
            <Form.ControlLabel>Период действия</Form.ControlLabel>

            <InputGroup>
              <Form.Control
                accepter={DatePicker}
                block
                oneTap
                isoWeek
                appearance="subtle"
                name="activeFrom"
                value={formState.activeFrom}
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="дата начала действия"
              />

              <InputGroup.Addon> до </InputGroup.Addon>

              <Form.Control
                accepter={DatePicker}
                block
                oneTap
                isoWeek
                appearance="subtle"
                name="activeUntil"
                value={formState.activeUntil}
                format="yyyy-MM-dd HH:mm:ss"
                placeholder="дата окончания действия"
              />
            </InputGroup>

            <Form.Group>
              <Form.ControlLabel>Вес</Form.ControlLabel>
              <Form.Control name="priority_weight" type="number" />
            </Form.Group>
          </Panel>
        </Col>
        
        <Col xs={24} md={16}>
          <SelectLocale 
            languages={formState.languages} 
            selectedLanguage={selectedLanguage}
            onSelect={(language) => setSelectedLanguage(language)}
            onAdd={(language) => {
              setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
            }}
          />
          
          <Panel 
            shaded
            className='my-5'
          >
            <LocalizedForm
              name='title'
              label='Название'
              selectedLanguage={selectedLanguage}
              value={formState.i18n?.find((translation) => translation.languageCode === selectedLanguage)?.title || ''}
              onChange={(value: string) => {
                const newTranslations = [ ...formState.i18n ]
                const titleIndex = newTranslations.findIndex(translation => translation.languageCode === selectedLanguage)
                if (titleIndex > -1) {
                  newTranslations[titleIndex].title = value
                }
                else {
                  newTranslations.push({ languageCode: selectedLanguage, title: value })
                }
                setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
              }}
            />
          </Panel>
          
        </Col>
      </Grid>

      <SaveButton
        loading={loadingTextMaterialTheme || loadingSaveTextTheme}
        disabled={loadingTextMaterialTheme || loadingSaveTextTheme}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
